<script lang="ts">
  /**
   * Phone-first frame from the proto: a 430px column centred on desktop with
   * a rounded "phone" border, full-bleed on mobile. Content scrolls inside
   * the frame; the bottom nav is pinned to the frame's bottom edge.
   */
  import { navigate, subscribe, type RouteDef } from '../lib/router';
  import type { AuthUser } from '../lib/auth';
  import { settings } from '../lib/settings.svelte';
  import { plans } from '../lib/plans.svelte';
  import Router from './Router.svelte';
  import Toast from './Toast.svelte';
  import Icon, { type IconName } from './Icon.svelte';
  import TodayView from '../views/TodayView.svelte';
  import MealView from '../views/MealView.svelte';
  import CalendarView from '../views/CalendarView.svelte';
  import ProgressView from '../views/ProgressView.svelte';
  import ProgramView from '../views/ProgramView.svelte';

  let { user }: { user: AuthUser } = $props();

  const routes: RouteDef[] = [
    { path: '/', component: TodayView },
    { path: '/paiva/:date', component: TodayView },
    { path: '/ateria/:date/:mealNo', component: MealView },
    { path: '/kalenteri', component: CalendarView },
    { path: '/kehitys', component: ProgressView },
    { path: '/ohjelma', component: ProgramView },
  ];

  const tabs: { path: string; label: string; icon: IconName; match: (p: string) => boolean }[] = [
    { path: '/', label: 'Tänään', icon: 'home', match: (p) => p === '/' || p.startsWith('/paiva/') || p.startsWith('/ateria/') },
    { path: '/kalenteri', label: 'Kalenteri', icon: 'calendar', match: (p) => p.startsWith('/kalenteri') },
    { path: '/kehitys', label: 'Kehitys', icon: 'trend', match: (p) => p.startsWith('/kehitys') },
    { path: '/ohjelma', label: 'Ohjelma', icon: 'plan', match: (p) => p.startsWith('/ohjelma') },
  ];

  let path = $state(window.location.pathname);
  let screenEl = $state<HTMLDivElement | null>(null);

  $effect(() => {
    const off = subscribe((p) => {
      path = p;
      // New screen: start at the top, like the proto's show().
      if (screenEl) screenEl.scrollTop = 0;
      window.scrollTo({ top: 0 });
    });
    return off;
  });

  $effect(() => {
    // Both are cached singletons; the views read them reactively.
    void settings.load();
    void plans.load();
  });
</script>

<div class="page">
  <div class="phone" data-user={user.sub}>
    <div class="screen" bind:this={screenEl}>
      <Router {routes} />
    </div>

    <nav class="nav" aria-label="Päävalikko">
      {#each tabs as t (t.path)}
        <button
          type="button"
          class:on={t.match(path)}
          aria-current={t.match(path) ? 'page' : undefined}
          onclick={() => navigate(t.path)}
        >
          <Icon name={t.icon} />
          {t.label}
        </button>
      {/each}
    </nav>

    <Toast />
  </div>
</div>

<style>
  .page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: var(--viisi-ground);
  }
  .phone {
    width: 100%;
    max-width: 430px;
    background: var(--viisi-ground);
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }
  /* Mobile: the body scrolls, so the screen stays overflow-visible and the
     sticky topbar sticks to the viewport. Desktop: the frame scrolls. */
  .screen {
    flex: 1;
    padding: 0 16px calc(96px + env(safe-area-inset-bottom, 0px));
  }
  .nav {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--viisi-nav-bg);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-top: 1px solid var(--viisi-line);
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    padding: 8px 6px calc(8px + env(safe-area-inset-bottom, 0px));
    z-index: 10;
  }
  .nav button {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    padding: 6px 0;
    font-size: 11px;
    font-weight: 600;
    color: var(--viisi-ink-3);
    border-radius: 10px;
  }
  .nav button :global(svg) { width: 22px; height: 22px; }
  .nav button.on { color: var(--viisi-accent); }

  @media (min-width: 640px) {
    .page { padding: 24px 0 48px; }
    .phone {
      min-height: 860px;
      height: 860px;
      border-radius: 40px;
      border: 1px solid var(--viisi-line);
      box-shadow: var(--viisi-shadow);
      overflow: hidden;
    }
    .screen { overflow-y: auto; -webkit-overflow-scrolling: touch; }
    .nav { position: absolute; }
  }
</style>
